import React, {useEffect, useMemo, useState} from 'react';
import {Button, SaveIcon} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';
import {useTaopix} from '../Context/TaopixContext';
import {
  ButtonProps,
  buttonSizeTypes,
  buttonStyles,
  labelAlignmentTypes,
} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {useSelector} from 'react-redux';
import {ProjectDirtySelector} from '../../Selectors';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';
import {CoreEvent} from '@taopix/taopix-core';

export type SaveButtonProps = {
  onProjectSaved?: () => void;
  className?: string;
} & Partial<
  Pick<
    ButtonProps,
    | 'label'
    | 'hideLabel'
    | 'buttonStyle'
    | 'corners'
    | 'style'
    | 'stacked'
    | 'labelAlignment'
    | 'startIcon'
    | 'size'
  >
>;

const SaveButton = ({onProjectSaved, className, ...rest}: SaveButtonProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const {t} = useTranslation();
  const projectDirty = useSelector(ProjectDirtySelector);

  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  const onSave = (event: React.MouseEvent) => {
    userFlow.activateUserFlow(UserFlowType.Save);
  };

  useEffect(() => {
    const token = app.core.subscribe(
      CoreEvent.PROJECT_SAVING,
      (msg: string, data: {showSaveOverlay: boolean; saving: boolean}) => {
        setSaveInProgress(data.saving);
      }
    );

    return () => {
      app.core.unsubscribe(token);
    };
  }, []);

  const buttonProps = {
    label: t('*:str_ButtonSave'),
    buttonStyle: 'standard' as buttonStyles,
    size: 'small' as buttonSizeTypes,
    stacked: true,
    labelAlignment: 'left' as labelAlignmentTypes,
    startIcon: <SaveIcon />,
    ...rest,
  };

  const saveButtonClasses = classNames('shrink-0', className);

  const buttonDisabled = useMemo(() => {
    // Save is in progress.
    if (saveInProgress) return true;

    // Project has not been named.
    if (ui.state.projectNaming.editName) return false;

    // The project is dirty
    return !projectDirty;
  }, [saveInProgress, projectDirty, ui.state.projectNaming.editName]);

  return (
    <Button
      onClick={onSave}
      disabled={buttonDisabled}
      loading={saveInProgress}
      className={saveButtonClasses}
      labelLines={1}
      {...buttonProps}
    />
  );
};

export default SaveButton;
